import { useState, useRef, useEffect } from "react";
import {
  Cloud, MapPin, Star, Home, Wind, Droplets, Umbrella, ChevronRight, Loader2, CloudOff, AlertTriangle,
  Search, Maximize2, Minimize2, GripVertical, LocateFixed,
} from "lucide-react";
import { SearchBar } from "./SearchBar";
import { FavoritesBar } from "./FavoritesBar";
import { WeatherIcon } from "./WeatherIcon";
import { useDraggableCard } from "../hooks/useDraggableCard";
import { temp, tempUnit, speed, speedUnit, windDirection, locationLabel, locationDetail, formatClock, formatHour } from "../lib/weather";

const LS_MODE = "aethera_card_mode";

const Metric = ({ Icon, label, value, testid, className = "" }) => (
  <div className="glass rounded-xl px-2.5 py-2 min-w-0">
    <div className="flex items-center gap-1 text-muted-foreground text-[10px]"><Icon className="w-3 h-3" />{label}</div>
    <div className={`font-bold mt-0.5 truncate ${className}`} data-testid={testid}>{value}</div>
  </div>
);

export const SummaryCard = ({
  data, selected, isNow, units, isFavorite, onToggleFavorite, isHome, onToggleHome, onOpenDetail,
  favorites, home, homeKey, activeKey, onSelectCity, onRemoveFav, liveData, loading, error, onRetry,
}) => {
  const [mode, setMode] = useState(() => localStorage.getItem(LS_MODE) || "compact");
  const [focusSearch, setFocusSearch] = useState(false);
  const ref = useRef(null);
  const expanded = mode === "expanded";
  const { style, dragging, desktop, moved, reset, handleProps } = useDraggableCard(ref, [mode, loading, !!data]);

  useEffect(() => localStorage.setItem(LS_MODE, mode), [mode]);
  useEffect(() => {
    if (expanded && focusSearch) {
      ref.current?.querySelector('[data-testid="city-search-input"]')?.focus();
      setFocusSearch(false);
    }
  }, [expanded, focusSearch]);

  const off = data?.location.timezone_offset || 0;
  const openSearch = () => {
    setMode("expanded");
    setFocusSearch(true);
  };

  return (
    <div
      ref={ref}
      style={style}
      className={`windy-summary ${expanded ? "is-expanded" : "is-compact"} ${dragging ? "is-dragging" : ""}`}
      data-testid="summary-card"
      data-mode={mode}
    >
      <div className={`flex items-center gap-2 mb-2 ${desktop ? "windy-drag-handle" : ""}`} data-testid="summary-card-handle" {...handleProps}>
        {desktop && <GripVertical className="w-4 h-4 text-muted-foreground/70 shrink-0" />}
        <div className="w-7 h-7 rounded-lg grid place-items-center bg-gradient-to-br from-indigo-500 to-cyan-400 shadow-lg shadow-indigo-500/30 shrink-0" data-testid="brand-logo">
          <Cloud className="w-3.5 h-3.5 text-white" strokeWidth={2} />
        </div>
        <div className="font-extrabold tracking-tight text-sm">KayongWeather</div>
        {!liveData && (
          <span data-testid="mock-data-badge" className="text-[9px] font-semibold text-amber-300 bg-amber-500/15 border border-amber-500/40 rounded-full px-1.5 py-0.5">
            Demo
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {!expanded && (
            <button data-testid="card-search-button" onClick={openSearch} title="Cari kota" className="windy-mini-btn">
              <Search className="w-3.5 h-3.5" />
            </button>
          )}
          {desktop && moved && (
            <button data-testid="card-reset-position" onClick={reset} title="Reset posisi kartu" className="windy-mini-btn">
              <LocateFixed className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            data-testid="card-mode-toggle"
            onClick={() => setMode(expanded ? "compact" : "expanded")}
            title={expanded ? "Perkecil" : "Perbesar"}
            className="windy-mini-btn"
          >
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {expanded && <SearchBar onSelect={onSelectCity} />}

      {loading && (
        <div className="grid place-items-center py-8 text-muted-foreground" data-testid="loading-state">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <p className="mt-2 text-xs">Memuat data cuaca...</p>
        </div>
      )}

      {error && !loading && (
        <div className="grid place-items-center py-6 text-muted-foreground" data-testid="error-state">
          <CloudOff className="w-7 h-7" />
          <p className="mt-2 text-xs">Gagal memuat data cuaca.</p>
          <button onClick={onRetry} className="mt-3 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold" data-testid="retry-button">
            Coba Lagi
          </button>
        </div>
      )}

      {data && !loading && !error && (
        <div className={expanded ? "mt-3 fade-up" : "fade-up"} data-testid="hero-weather-card">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
                <span data-testid="current-location-name" className="font-semibold text-sm truncate max-w-[180px]">
                  {locationLabel(data.location)}
                </span>
                {isHome && (
                  <span data-testid="home-city-badge" className="text-[9px] font-semibold uppercase tracking-wider bg-primary/15 text-primary rounded-full px-1.5 py-0.5">
                    Rumah
                  </span>
                )}
              </div>
              {locationDetail(data.location) && (
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate" data-testid="current-location-detail" title={locationDetail(data.location)}>
                  {locationDetail(data.location)}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground num mt-0.5" data-testid="selected-time-label">
                {isNow ? `Sekarang · ${formatClock(data.current.dt, off)} waktu lokal` : `Prakiraan · ${formatHour(selected.dt, off)} waktu lokal`}
              </div>
            </div>
            {expanded && (
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  data-testid="home-city-button"
                  onClick={onToggleHome}
                  title={isHome ? "Hapus sebagai kota rumah" : "Jadikan Kota Rumah"}
                  className={`h-8 w-8 grid place-items-center rounded-xl transition-colors ${isHome ? "bg-primary text-primary-foreground" : "glass hover:border-primary/40"}`}
                >
                  <Home className={`w-4 h-4 ${isHome ? "" : "text-muted-foreground"}`} />
                </button>
                <button
                  data-testid="favorite-city-button"
                  onClick={onToggleFavorite}
                  title={isFavorite ? "Hapus dari favorit" : "Simpan ke favorit"}
                  className="h-8 w-8 grid place-items-center glass rounded-xl hover:border-primary/40 transition-colors"
                >
                  <Star className={`w-4 h-4 ${isFavorite ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                </button>
              </div>
            )}
          </div>

          <div className="mt-2 flex items-center gap-3">
            <WeatherIcon icon={selected.condition.icon} spin className={`${expanded ? "w-16 h-16" : "w-12 h-12"} float-y shrink-0`} style={{ color: "var(--accent-color)" }} />
            <div className="min-w-0">
              <div data-testid="current-temperature-display" className={`${expanded ? "text-5xl" : "text-4xl"} font-black tracking-tighter num leading-none`}>
                {temp(selected.temp, units)}
                <span className="text-xl align-top text-muted-foreground">{tempUnit(units)}</span>
              </div>
              <div className="mt-1 text-xs font-semibold capitalize truncate">{selected.condition.description}</div>
              {expanded && (
                <div className="text-xs text-muted-foreground num">Terasa {temp(selected.feels_like, units)}{tempUnit(units)}</div>
              )}
            </div>
          </div>

          <div className="mt-2.5 grid grid-cols-3 gap-1.5 text-xs num" data-testid="summary-metrics">
            <Metric Icon={Wind} label="Angin" testid="summary-wind" value={`${speed(selected.wind_speed, units)} ${speedUnit(units)} ${windDirection(selected.wind_deg)}`} />
            <Metric Icon={Droplets} label="Lembap" testid="summary-humidity" value={`${selected.humidity}%`} />
            <Metric Icon={Umbrella} label="Hujan" testid="summary-pop" value={`${selected.pop}%`} className="text-sky-400" />
          </div>

          {expanded && (
            <>
              {data.alerts && data.alerts.length > 0 && (
                <div className="mt-3 flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2" data-testid="summary-alert">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{data.alerts[0].event}</span>
                </div>
              )}
              <div className="mt-3">
                <FavoritesBar favorites={favorites} home={home} homeKey={homeKey} activeKey={activeKey} onSelect={onSelectCity} onRemove={onRemoveFav} />
              </div>
              <button
                data-testid="open-detail-button"
                onClick={onOpenDetail}
                className="mt-3 w-full flex items-center justify-between rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:brightness-110 transition"
              >
                Detail prakiraan, AQI &amp; 7 hari
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
